package us.laantech.common.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

data class DataTableColumn<T>(
    val header: String,
    val weight: Float = 1f,
    val cell: @Composable (T) -> Unit,
)

class DataTableScope<T> {
    internal val columns = mutableListOf<DataTableColumn<T>>()

    fun column(
        header: String,
        weight: Float = 1f,
        cell: @Composable (T) -> Unit,
    ) {
        columns.add(DataTableColumn(header, weight, cell))
    }
}

/**
 * Example Usage:
 * ```
 *         DataTableView<InternalClientDepartment>(
 *             modifier = Modifier.fillMaxSize().weight(2f),
 *             columns = listOf(
 *                 column("Department Name", weight = 2f) {
 *                     Text(it.departmentName, modifier = Modifier.weight(2f))
 *                 },
 *                 column("Department UUID", weight = 2f) {
 *                     Text(it.departmentUUID, modifier = Modifier.weight(2f))
 *                 },
 *                 column("Age Limit", weight = 1f) {
 *                     Text(it.ageLimit.toString(), modifier = Modifier.weight(1f))
 *                 },
 *                 column("Actions", weight = 1f) {
 *                     Button(onClick = { /* handle edit */ }) {
 *                         Text("Edit")
 *                     }
 *                 },
 *             ),
 *             rows = uiState.departments.toList(),
 *         )
 * ```
 */
@Composable
fun <T> DataTableView(
    modifier: Modifier = Modifier,
    headerBackground: Color = Color.Gray,
    rowBackground: Color = Color.LightGray,
    rows: List<T>,
    columnsBlock: DataTableScope<T>.() -> Unit
) {

    val columns = DataTableScope<T>().apply(columnsBlock).columns

    val rowBackgroundEven = rowBackground.copy(alpha = 0.5f)
    val rowBackgroundOdd = rowBackground
    val headerTextColor = if (headerBackground.luminance() > 0.5f) Color.Black else Color.White
    val rowTextColor = if (rowBackground.luminance() > 0.5f) Color.Black else Color.White

    Column(modifier = modifier) {
        // Header
        Row(modifier = Modifier.background(headerBackground), verticalAlignment = Alignment.CenterVertically) {
            columns.forEachIndexed { index, col ->

                val align =
                    if (index == 0) TextAlign.Start
                    else TextAlign.Center

                Text(col.header,
                    modifier = Modifier
                        .weight(col.weight)
                        .padding(start = 4.dp, end = 4.dp),
                    textAlign = align,
                    color = headerTextColor)
            }
        }

        LazyColumn() {
            // Rows
            itemsIndexed(rows) { index, row ->
                val isEven = index % 2 == 0
                val background = if (isEven) rowBackgroundEven else rowBackgroundOdd

                Row(modifier = Modifier
                    .background(background)
                    .padding(start = 4.dp, end = 4.dp),
                    verticalAlignment = Alignment.CenterVertically
                    ) {

                    CompositionLocalProvider(LocalContentColor provides rowTextColor) {
                        // Row alignment must be done in the Row composable
                        columns.forEachIndexed { index, col ->
                            val align =
                                if (index == 0) TextAlign.Start
                                else TextAlign.Center

                            CompositionLocalProvider(LocalTextStyle provides LocalTextStyle.current.copy(textAlign = align)) {
                                col.cell(row)
                            }
                        }
                    }

                }
            }
        }
    }

}